import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { components } from 'react-select'
import DownIcon from '../../../assets/icons/downbig.svg'
import DropDownIcon from '../../../assets/icons/drop-down-white.svg'
import theme from '../../../theme'
import StyledSelect from './StyledSelect'
import { Caption2, paragraph1CommonStyles, paragraph2CommonStyles, B1, B2, P2, H6 } from '../../Typo'
import { up } from 'styled-breakpoints'
import { pxToRem } from '../../../utils'
import CheckboxTick from '../../../assets/icons/checkbox-tick.svg'

export const SizeVariant = {
  SMALL: 'small',
  REGULAR: 'regular'
}

const SelectDropdown = props => {
  const {
    menuIsOpen,
    name,
    label,
    error,
    errorMsg,
    options,
    placeholder,
    value,
    required,
    shouldShowAsterisk,
    innerRef,
    onChange,
    onBlur,
    onFocus,
    defaultValue,
    disabled,
    inputId,
    isMulti,
    testId = 'ui-select',
    readOnly,
    size = SizeVariant.REGULAR,
    withSearch,
    clearable = false,
    className,
    shouldShowTick,
    dropdownStyling,
    rotateDropdownIcon,
    isStyledDropdownIcon,
    suffix,
    secondaryPlaceholder,
    inputFieldSuffix,
    isMobile
  } = props

  const [focused, setFocused] = useState(false)

  // To keep track of the rotation of the icon based on if the menu is open or not(only for few dropdowns).
  const [isMenuOpenTransition, setisMenuOpenTransition] = useState(false)
  const onIconTransitionClick = () => {
    // Rotate dropdown icon only if props passed for rotating it or not
    rotateDropdownIcon && setisMenuOpenTransition(!isMenuOpenTransition)
  }

  const isControlled = innerRef && onChange ? true : false

  const onClickHandler = evt => {
    setFocused(true)
    onIconTransitionClick()
  }

  const onChangeHandler = value => {
    if (isControlled) {
      onChange(value)
    }
  }
  const onFocusHandler = e => {
    setFocused(true)
    onFocus(e)
  }
  const onBlurHandler = e => {
    setFocused(false)
    onBlur(e)
    onIconTransitionClick()
  }

  return (
    <InputWrapper data-testid={testId} size={size}>
      {label && (
        <InputLabel
          disabled={disabled}
          error={error}
          data-value={value}
          value={value}
          focused={focused}
          htmlFor={`select-${label}`}
          id={`basic-select-${label}`}
          defaultValue={defaultValue}
          onClick={onClickHandler}
          size={size}
          className={className}
        >
          {DisplayLabel(required, label, disabled, shouldShowAsterisk)}
        </InputLabel>
      )}
      {secondaryPlaceholder && <RightPlaceholder>{secondaryPlaceholder}</RightPlaceholder>}

      {/* When clicked on the dropdown i.e when the dropdown is open/closed accordingly rotate the 
          dropdown icon(only for certain dropdowns) */}
      <StyledSelectWrapper
        onClick={onIconTransitionClick}
        styledSelectWrapper={props.dropdownStyling.styledSelectWrapper}
      >
        <StyledSelect
          hideSelectedOptions={false}
          size={size}
          isMulti={isMulti}
          defaultValue={value}
          style={{
            option: {
              padding: 0
            }
          }}
          menuIsOpen={menuIsOpen}
          isClearable={!required || clearable}
          id={`styled-select-${label}`}
          data-testid={`styled-select-${label}`}
          ref={innerRef}
          closeMenuOnSelect={!isMulti}
          inputId={inputId}
          value={value}
          isDisabled={disabled}
          placeholder={placeholder}
          onChange={onChangeHandler}
          onFocus={onFocusHandler}
          onClickHandler={onClickHandler}
          onBlur={onBlurHandler}
          name={name}
          options={options}
          className={className}
          shouldShowTick={shouldShowTick}
          error={error}
          isSearchable={withSearch}
          rotateDropdownIcon={rotateDropdownIcon}
          isMenuOpenTransition={isMenuOpenTransition}
          isStyledDropdownIcon={isStyledDropdownIcon}
          styles={getSelectDropdownCustomStyles(
            error,
            disabled,
            focused,
            value,
            readOnly,
            size,
            isMulti,
            className,
            dropdownStyling,
            isMobile
          )}
          components={{
            DropdownIndicator: readOnly ? () => null : DropdownIndicator,
            IndicatorSeparator: () => null,
            Option,
            MenuList,
            MultiValue: props => {
              const selectedValues = props.getValue()

              const shouldNotAppendComma = selectedValues.length === props.index + 1
              return (
                <>
                  <MultiValueLabel isMulti={isMulti} title={props.data.label}>
                    {props.data.label}
                  </MultiValueLabel>
                  {shouldNotAppendComma ? '' : <span>, &nbsp;</span>}
                </>
              )
            }
          }}
        />
      </StyledSelectWrapper>
      {error && errorMsg?.length > 0 && !focused && <ErrorText className="error-text">{errorMsg}</ErrorText>}
      {inputFieldSuffix && <StyledP>{inputFieldSuffix}</StyledP>}
    </InputWrapper>
  )
}

const RightPlaceholder = styled(B2)`
  padding-top: 28px;
  position: absolute;
  right: 28px;
  bottom: 22px;
  margin-block-end: 0;
  margin-block-start: 0;
  color: ${props => props.theme.palette.grey2};
  ${up('md')} {
    right: ${pxToRem(40)};
  }
`

const StyledP = styled(P2)`
  font-style: italic;
  color: ${props => props.theme.palette.grey2};
`

const StyledSelectWrapper = styled.div`
  ${up('md')} {
    padding-top: ${props => props.styledSelectWrapper?.paddingTop || '0'};
  }
`

const DisplayLabel = (required, label, disabled, shouldShowAsterisk) => {
  return (
    <>
      {label + ' '}
      {shouldShowAsterisk && required && <Required disabled={disabled}>*</Required>}
    </>
  )
}
const getBorderWidth = ({ error, disabled, focused }) => {
  if (disabled) return '1px'
  if (focused && !error) return '2px'
  if (error && !disabled) return '2px'
  if (!error && !focused && !disabled) return '1px'
}

const getTopSpacing = (className, isMobile) => {
  /* Based on the className given to the dropdown component apply the CSS property for both mobile and web device type.
     NOTE: className used coz if there could be multiple dropdowns where top value will be different so can be added here in the code */
  if (className === 'explore-fund-dropdown') {
    if (isMobile) return '15%'
    return '20%'
  }
  return '20%' // This is the default top value picked from marketplace-ui-componenr
}

const getDropdownMarginBottom = (className, isMobile) => {
  // ClassName based give margin-bottom value to the dropdown
  if (className === 'explore-fund-dropdown') {
    if (isMobile) return '4px'
    return '0'
  }
  return '0' // Default margin-bottom value of the dropdown component
}

const getDropdownMarginTop = (className, isMobile) => {
  if (className === 'explore-fund-dropdown') {
    if (isMobile) return '0'
    return '4px'
  }
  return '0' // Default margin-top value of the dropdown component
}

const getBorderColor = props => {
  const { error, disabled, focused, theme, value, readOnly, className } = props
  // here change needed
  const isInvestmentIdDropdown = className === 'investment-id-dropdown'
  const isDropDownV2 = className === 'dropdown-v2'

  if (disabled) return theme.palette.grey5
  if (readOnly) return theme.palette.white
  if (error && !focused) {
    return theme.palette.red1
  }

  if (error && focused) return theme.palette.grey3

  if (error && !value && !focused) {
    if (isInvestmentIdDropdown) {
      return theme.palette.white
    } else if (isDropDownV2) {
      return theme.palette.grey9
    } else {
      return theme.palette.grey5
    }
  }

  if (focused && !error) {
    if (isInvestmentIdDropdown) {
      return theme.palette.white
    } else if (isDropDownV2) {
      return theme.palette.grey9
    } else {
      return theme.palette.grey6
    }
  }

  if (!error && !disabled && !focused && !value) {
    if (isInvestmentIdDropdown) {
      return theme.palette.white
    } else if (isDropDownV2) {
      return theme.palette.grey9
    } else {
      return theme.palette.grey3
    }
  } else if (!error && !disabled && !focused && value) {
    if (isInvestmentIdDropdown) {
      return theme.palette.whiteGrey7
    } else if (isDropDownV2) {
      return theme.palette.grey9
    } else {
      return theme.palette.grey3
    }
  }
}

const valueContainerMultiSelectStyles = styles => ({
  ...styles,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'flex',
  fontWeight: 'bold',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  marginRight: '12px'
})

const getSelectDropdownCustomStyles = (
  error,
  disabled,
  focused,
  value,
  readOnly,
  size,
  isMulti,
  className,
  dropdownStyling,
  isMobile
) => ({
  singleValue: styles => ({
    ...styles,
    top: `${getTopSpacing(className, isMobile)}`,
    // Based on props, if the styling is provided by props then apply it if not provided use default values
    // from marketplace dropdown styling
    paddingRight: dropdownStyling.singleValue.paddingRight || '42px',
    marginLeft: dropdownStyling.singleValue.marginLeft || '2px',
    fontWeight: 'bold',
    color: disabled ? theme.palette.grey6 : dropdownStyling.singleValue.color || theme.palette.grey1,
    transform: `translateY(calc(-50% - ${size === SizeVariant.SMALL ? '2px' : '0'}))`
  }),

  control: styles => ({
    ...styles,
    backgroundColor: 'inherit',
    border: 'none',
    borderRadius: 0,
    outline: 'none',
    borderBottom: `${getBorderWidth({ error, disabled, focused })} solid ${dropdownStyling.control.borderBottomColor ||
      getBorderColor({
        error,
        disabled,
        focused,
        theme,
        value,
        readOnly,
        className
      })}`,
    marginTop: readOnly ? '12px' : dropdownStyling.control.marginTop || '16px',
    minHeight: size === SizeVariant.SMALL ? 'auto' : '33px',
    boxShadow: 'none',
    '& path': {
      fill: disabled ? theme.palette.grey4 : dropdownStyling.control.fillSVGcolor || theme.palette.grey3
    },
    '&:hover': {
      borderBottom: dropdownStyling.control.borderBottomHoverColor || `2px solid ${theme.palette.grey1}`,
      '& path': {
        fill: dropdownStyling.control.fillHoveredSVGcolor || theme.palette.grey1
      }
    },
    '&:focus': {
      borderBottom: dropdownStyling.control.borderBottonFocusColor || `2px solid ${theme.palette.grey1}`,
      '& path': {
        fill: dropdownStyling.control.fillFocusedSVGcolor || theme.palette.grey1
      }
    },
    '&:focus-within': {
      borderBottom: dropdownStyling.control.borderBottomFocusWithinColor || `2px solid ${theme.palette.grey1}`
    }
  }),
  option: styles => ({
    padding: 0,
    '&:active': {
      background: 'none'
    }
  }),
  valueContainer: styles => ({
    padding: '0',
    ...(isMulti ? valueContainerMultiSelectStyles(styles) : {})
  }),
  menuList: styles => ({
    padding: '0',
    maxHeight: dropdownStyling.menuList.maxHeight || '156px',
    overflowY: 'auto',
    borderRadius: '12px'
  }),
  menu: styles => ({
    backgroundColor: `${theme.palette.white}`,
    marginTop: dropdownStyling.menu.marginTop || '0',
    borderRadius: '0px',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    // Apply top radius for Explore fund dropdown
    borderTopLeftRadius: dropdownStyling.menu.borderTopLeftRadius || '0',
    borderTopRightRadius: dropdownStyling.menu.borderTopRightRadius || '0',
    boxShadow: dropdownStyling.menu.boxShadow || '0 4px 20px rgba(96, 96, 96, 0.2)',
    position: 'relative',
    zIndex: 11,

    // For explore fund dropdown
    left: dropdownStyling?.menu?.left || '0',
    width: dropdownStyling?.menu?.width || 'auto'
  }),
  input: styles => ({
    ...styles,
    marginTop: size === SizeVariant.SMALL ? '0' : '2px',
    paddingTop: size === SizeVariant.SMALL ? '0' : '2px'
    // display: 'inline-block'
  }),
  indicatorsContainer: styles => ({
    ...styles,
    alignItems: 'center'
  }),
  dropdownIndicator: styles => ({
    ...styles,
    height: '20px',
    padding: dropdownStyling.dropdownIndicator.padding || '8px',
    marginRight: dropdownStyling.dropdownIndicator.marginRight || '0',
    marginBottom: `${getDropdownMarginBottom(className, isMobile)}`,
    marginTop: `${getDropdownMarginTop(className, isMobile)}`
  }),
  clearIndicator: styles => ({
    ...styles,
    padding: '0px',
    height: '20px',
    display: dropdownStyling?.clear?.display
  })
})

const ErrorText = styled(Caption2)`
  color: ${props => props.theme.palette.red1};
`

const StyledDownIcon = styled(DownIcon)`
  transition-duration: 0.8s;

  /* When the classname given to the Icon is down-arrow then rotate the icon 90 degrees to point the 
arrow to bottom. After this transformation done arrow will now point to downwards.(Dropdown not visible in this state of Icon) */
  &.down-arrow {
    transform: rotate(0deg);
  }
  /* When classname of down-arrow is replaced with up-arrow then rotate 90 degrees anti-clockwise to
 get back the arrow to its original pointing position i.e upwards.(In this state, Dropdown options are visible) */
  &.up-arrow {
    transform: rotate(-180deg);
  }
`

const StyledDropDownIcon = styled(DropDownIcon)`
  transition-duration: 0.8s;
  &.down-arrow {
    transform: rotate(0deg);
  }
  &.up-arrow {
    transform: rotate(180deg);
  }
`

const DropdownIndicator = props => {
  // const isInvestmentIdDropdown = props.selectProps.className.includes('investment-id-dropdown')
  const isStyledDropdownIcon = props.selectProps.isStyledDropdownIcon
  return (
    <components.DropdownIndicator {...props}>
      {isStyledDropdownIcon ? (
        <StyledDropDownIcon className={props.selectProps.isMenuOpenTransition ? 'up-arrow' : 'down-arrow'} />
      ) : (
        <StyledDownIcon className={props.selectProps.isMenuOpenTransition ? 'up-arrow' : 'down-arrow'} />
      )}
    </components.DropdownIndicator>
  )
}

const Option = props => {
  return (
    <components.Option {...props} style={{ padding: 0 }}>
      <StyledOption
        className={props.selectProps.className}
        shouldShowTick={props.selectProps.shouldShowTick}
        isMulti={props.isMulti}
        data-testid={`option-${props.selectProps.name}-${props.data.value}`}
      >
        {props.isMulti && (
          <SimpleCheckbox data-testid="simple-checkbox" checked={props.isSelected}>
            <Tick />
          </SimpleCheckbox>
        )}
        <>
          {/* {getOptionText(props)} */}
          <>
            {getOptionText(props)}
            {props.data.secondaryLabelName && (
              <StyledSecondaryOptionP2>{props.data.secondaryLabelName}</StyledSecondaryOptionP2>
            )}
          </>
        </>
      </StyledOption>
    </components.Option>
  )
}

const StyledSecondaryOptionP2 = styled(P2)`
  color: ${props => props.theme.palette.grey2};
`

// Provide tick to the option that is selected by the user. Show only if it is passed as props
const getOptionText = props => {
  if (props.selectProps.shouldShowTick) {
    return (
      <OptionsWrapper>
        {/* Bold the option which is chosen/selected by the user */}
        <div>{props.isSelected ? <H6>{props.data.label}</H6> : props.data.label}</div>
        <SimpleTick checked={props.isSelected}>{props.isSelected && <SelectTick />}</SimpleTick>
      </OptionsWrapper>
    )
  } else {
    return <>{props.data.label}</>
  }
}

const MenuList = props => {
  return (
    <components.MenuList {...props}>
      <StyledMenuList data-testid={`options-list-${props.selectProps.name}`}>{props.children}</StyledMenuList>
    </components.MenuList>
  )
}

const Required = styled.span`
  color: ${props => (props.disabled ? props.theme.palette.grey6 : props.theme.palette.blue1)};
`

const StyledMenuList = styled.div`
  padding: 12px 0;
`
const StyledOption = styled.div`
  color: ${props => props.theme.palette.grey2};
  font-size: 16px;
  line-height: 24px;
  min-height: 40px;
  padding: 8px 0px 8px 12px;
  background-color: ${props => props.theme.palette.white};
  & :active,
  :hover {
    color: ${props => props.theme.palette.grey1};
    background-color: ${props => props.theme.palette.grey5};
  }

  ${props =>
    props.isMulti &&
    css`
      display: flex;
      align-items: center;
    `}
  &.dropdown-v2 {
    font-family: Lato;
    color: ${props => props.theme.palette.black3};
    font-size: 14px;
    cursor: pointer;
    font-weight: bold;
    padding-top: 0;
    /* padding-bottom: 0; */
    & :active,
    :hover {
      color: ${props => props.theme.palette.black3};
      background-color: ${props => props.theme.palette.white};
    }
  }
`

const InputWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  min-height: 48px;
  position: relative;
  height: ${pxToRem(62)};
  ${up('md')} {
    height: ${({ size }) => (size === SizeVariant.SMALL ? pxToRem(62) : pxToRem(74))};
  }
`

const getLabelColor = props => {
  const { error, disabled, focused, theme } = props
  if (disabled) {
    return theme.palette.grey6
  }
  if (error && !focused) {
    return theme.palette.red1
  } else if (!error && !disabled) {
    return theme.palette.grey3
  }
}

const getLabelFloatingFontStyles = ({ size }) =>
  size === SizeVariant.SMALL &&
  css`
    ${up('md')} {
      font-size: ${pxToRem(12)};
      line-height: ${pxToRem(16)};
    }
  `

const getLabelFontStyles = ({ size }) =>
  size === SizeVariant.SMALL &&
  css`
    ${up('md')} {
      transform: translateY(16px);
      font-size: ${pxToRem(14)};
      line-height: ${pxToRem(20)};
    }
  `

const InputLabel = styled.label`
  transition: transform 150ms ease-in;
  transform-origin: top left;
  color: ${props => getLabelColor(props)};
  ${props =>
    props.focused || props.value || props.defaultValue
      ? css`
          ${paragraph2CommonStyles};
          ${getLabelFloatingFontStyles};
        `
      : css`
          ${paragraph1CommonStyles};
          transform: translateY(20px);
          ${getLabelFontStyles};
        `};
  position: absolute;
  top: 0;
  left: 0;
  display: block;

  // dropdown-v2 class is used to apply styling changes to the dropdown as per PFM-v3 dropdownDesign
  &.dropdown-v2 {
    transition: none;
    transform-origin: none;
    transform: none;
    position: normal;
    font-size: ${pxToRem(12)};
    color: ${props => props.theme.palette.grey3};
    ${up('md')} {
      margin-bottom: ${pxToRem(12)};
      font-size: ${pxToRem(14)};
    }
  }

  &.investment-id-dropdown {
    color: ${theme.palette.white};
    opacity: 0.7;
    ${up('md')} {
      opacity: 0.8;
    }
  }
`

const MultiValueLabel = styled.span`
  vertical-align: middle;
  display: inline-block;
  font-weight: bold;
`

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  // Hide checkbox visually but remain accessible to screen readers.
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const Tick = styled(CheckboxTick)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const OptionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const SelectTick = styled(Tick)`
  top: 70%;
  right: 0;
  left: 0;
`

const styledCheckboxWidthHeight = css`
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};

  ${up('md')} {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }
`

const SimpleCheckbox = styled.div`
  ${styledCheckboxWidthHeight};
  box-sizing: border-box;
  position: relative;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colors.primary};
  display: inline-block;
  transition: all 150ms;
  min-width: 18px;
  opacity: ${props => (props.disabled && props.checked ? 0.4 : 1)};
  margin-left: 4px;
  margin-right: 12px;
  background: ${props => (props.checked ? props.theme.colors.primary : props.theme.palette.white)};
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 4px 0 rgba(237, 17, 100, 0.4);
  }
  ${Tick} {
    visibility: ${props => (props.checked ? 'visible' : 'hidden')};
  }
`

const SimpleTick = styled.div`
  ${styledCheckboxWidthHeight};
  /* Show tick for any of the option whichever is selected or else will show only one tick placed in uncontrolled position. */
  position: relative;
`

SelectDropdown.defaultProps = {
  label: '',
  name: '',
  error: null,
  required: false,
  onChange: e => {},
  onFocus: e => {},
  onBlur: e => {},
  withSearch: false,
  isMulti: false,
  shouldShowAsterisk: false,
  /* Define dropdown styling objects. If in case props has more styling then add them here also(IMP) */
  dropdownStyling: { singleValue: {}, control: {}, menu: {}, menuList: {}, dropdownIndicator: {} },
  rotateDropdownIcon: false,
  isStyledDropdownIcon: false
}

SelectDropdown.propTypes = {
  required: PropTypes.bool,
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  error: PropTypes.bool,
  errorMsg: PropTypes.string,
  innerRef: PropTypes.any,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  withSearch: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  shouldShowAsterisk: PropTypes.bool, // To either show asterisk or not for the required dropdown component
  className: PropTypes.string, // Additional props added to denote whether it's a invesmentID dropdown or general dropdown
  shouldShowTick: PropTypes.bool, // Whether to show tick on the selection dropdown value
  dropdownStyling: PropTypes.object, // Custom styling for the whole dropdown component
  rotateDropdownIcon: PropTypes.bool, // Whether to rotate dropdown icon when the dropdown is closed/opened
  menuIsOpen: PropTypes.bool, // Boolean to open/close dropdown options menu
  isStyledDropdownIcon: PropTypes.bool, // Boolean to either show old dropdown icon from marketplace-ui or the updated/styled one
  secondaryPlaceholder: PropTypes.string, // field is used to display secondary placeholder towards dropdown icon
  inputFieldSuffix: PropTypes.string,
  suffix: PropTypes.string, // is used to display a label below the dropdown input field
  isMobile: PropTypes.bool // To check if its a mobile or desktop device
}

export default SelectDropdown
